export type CanonicalPrimitiveLegacyBand =
  | 'auto'
  | 'suggested'
  | 'possible'
  | 'unknown'
  | 'overridden';

export type CanonicalPrimitiveStatus = 'confirmed' | 'proposed' | 'unknown';

export const NOT_A_PRIMITIVE_CANONICAL = 'not_a_primitive';

export function statusFromLegacyBand(
  band: CanonicalPrimitiveLegacyBand,
): CanonicalPrimitiveStatus {
  if (band === 'overridden') return 'confirmed';
  if (band === 'unknown') return 'unknown';
  return 'proposed';
}

export function effectiveCanonicalStatus(input: {
  status?: CanonicalPrimitiveStatus;
  band: CanonicalPrimitiveLegacyBand;
  override?: unknown;
}): CanonicalPrimitiveStatus {
  if (input.status) return input.status;
  if (input.override) return 'confirmed';
  return statusFromLegacyBand(input.band);
}

export function bandForStatus(
  status: CanonicalPrimitiveStatus,
): CanonicalPrimitiveLegacyBand {
  if (status === 'confirmed') return 'overridden';
  if (status === 'unknown') return 'unknown';
  return 'suggested';
}

export function canonicalStatusLabel(status: CanonicalPrimitiveStatus): string {
  if (status === 'confirmed') return 'Confirmed';
  if (status === 'proposed') return 'Proposed';
  return 'Unknown';
}

/**
 * The single user-facing classification model: every component is a
 * Primitive, a Suggested primitive (awaiting review), Not a primitive, or
 * Not reviewed. This is the one vocabulary the UI should speak — `tier`,
 * `band`, and raw `status` are implementation details behind it.
 */
export type PrimitiveBucket =
  | 'primitive'
  | 'suggested'
  | 'not-primitive'
  | 'not-reviewed';

interface BucketInput {
  status?: CanonicalPrimitiveStatus;
  band: CanonicalPrimitiveLegacyBand;
  override?: unknown;
  canonical: string;
}

export function primitiveBucket(
  classification: BucketInput | null | undefined,
): PrimitiveBucket {
  if (!classification) return 'not-reviewed';
  const isNotPrimitive = classification.canonical === NOT_A_PRIMITIVE_CANONICAL;
  const status = effectiveCanonicalStatus(classification);
  if (status === 'confirmed') {
    return isNotPrimitive ? 'not-primitive' : 'primitive';
  }
  if (status === 'proposed' && !isNotPrimitive) return 'suggested';
  return 'not-reviewed';
}

export const PRIMITIVE_BUCKET_LABEL: Record<PrimitiveBucket, string> = {
  primitive: 'Primitive',
  suggested: 'Suggested',
  'not-primitive': 'Not a primitive',
  'not-reviewed': 'Not reviewed',
};

/** A bucket reflects a deliberate human/confident decision (vs. pending work). */
export function isClassifiedBucket(bucket: PrimitiveBucket): boolean {
  return bucket === 'primitive' || bucket === 'not-primitive';
}

/** Buckets that still need a human decision, ordered by review priority. */
export function needsReviewBucket(bucket: PrimitiveBucket): boolean {
  return bucket === 'suggested' || bucket === 'not-reviewed';
}

/**
 * Whether the Components detail page should render in primitive-shaped or
 * custom-shaped chrome. Status-based: confirmed/proposed canonical → primitive;
 * unknown / not-a-primitive / no classification → custom (precision over recall,
 * per primitive-discovery-pivot). Tier (`core` | `composition`) acts as a
 * fallback only when there is no classification at all — keeps the behaviour
 * sane for ingested repos before the classifier runs.
 */
export type EffectivePageMode = 'primitive' | 'custom';

export function resolveEffectivePageMode(input: {
  classification: {
    status?: CanonicalPrimitiveStatus;
    band: CanonicalPrimitiveLegacyBand;
    override?: unknown;
    canonical: string;
  } | null;
  tier?: 'core' | 'composition';
}): EffectivePageMode {
  const c = input.classification;
  if (c && c.canonical === NOT_A_PRIMITIVE_CANONICAL) return 'custom';
  if (c) {
    const status = effectiveCanonicalStatus(c);
    if (status === 'confirmed' || status === 'proposed') return 'primitive';
  }
  return input.tier === 'core' ? 'primitive' : 'custom';
}
